import { download } from '@app/utils'

import { exportContractorsCluster } from '@app/api/routes/contractors'
import {
  exportQuotas,
  exportQuota,
  exportTemplateQuotaReport,
  exportQuotaReport,
  exportQuotaReports
} from '@app/api/routes/quotaReports'
import {
  exportArchivesItem,
  exportCompareArchives,
  exportMonitoringArchivesLists,
  exportMonitoringList,
  exportMonitoringLists,
  getListsItemsProductsExportTemplate
} from '@app/api/routes/monitoringLists'
import { exportMonitoringListsProductsTemplate } from '@app/api/routes/monitoringListsProducts'
import {
  exportAllMonitoringResults,
  exportAllMonitoringsRegistry,
  exportAllMonitoringTasks,
  exportCheckingPrices,
  exportTechChecking,
  exportListByIds,
  exportMatchingTemplate,
  exportTemplateMonitoringLists,
  exportTaskValidationsTechCheck
} from '@app/api/routes/monitoringTasks'
import { exportMonitoringValidationStoresLinks } from '@app/api/routes/monitoringValidation'

import { createAwaitReportCompletionFn } from '@app/entities/report'

import { CONTENT_TYPES, FILE_TYPES } from '@app/api/constants'
import { exportMonitoringCategoriesKinds } from '@app/api/routes/monitorings'

export interface ExportFileOptions {
  signal?: AbortSignal
  fileType?: string
}

export type ExportFn<ParamsType = unknown> = (
  fileName: string,
  params: ParamsType,
  options?: ExportFileOptions
) => Promise<void>

export type AsyncResponseFn<ParamsType = unknown> = (params: ParamsType, signal?: AbortSignal) => Promise<Blob>

export const exportFileTypeMap: Record<string, string> = {
  [CONTENT_TYPES.applicationOfficedocumentSpreadsheetml]: FILE_TYPES.xlsx,
  [CONTENT_TYPES.applicationZip]: FILE_TYPES.zip,
  [CONTENT_TYPES.textCsv]: FILE_TYPES.csv
}

const exportFileFactory = <ParamsType>(asyncFn: AsyncResponseFn<ParamsType>): ExportFn<ParamsType> => {
  return async (fileName, params, options: ExportFileOptions = {}): Promise<void> => {
    const { signal, fileType } = options

    const file: Blob = await asyncFn(params, signal)

    const type: string = fileType ?? exportFileTypeMap[file.type] ?? file.type

    await download(file, { type, fileName })
  }
}

export const exportMonitoringListFile = exportFileFactory(exportMonitoringList)
export const exportMonitoringListsFile = exportFileFactory(createAwaitReportCompletionFn(exportMonitoringLists))
export const exportMonitoringArchivesListsFile = exportFileFactory(exportMonitoringArchivesLists)
export const exportContractorsClusterFile = exportFileFactory(exportContractorsCluster)
export const exportQuotasFile = exportFileFactory(exportQuotas)
export const exportQuotaReportsFile = exportFileFactory(exportQuotaReports)
export const exportMatchingTemplateFile = exportFileFactory(exportMatchingTemplate)
export const exportTemplateProductFile = exportFileFactory(getListsItemsProductsExportTemplate)
export const exportMonitoringListsProductsTemplateFile = exportFileFactory(exportMonitoringListsProductsTemplate)
export const exportContractorsListFile = exportFileFactory(exportListByIds)
export const exportTaskValidationsTechCheckFile = exportFileFactory(exportTaskValidationsTechCheck)
export const exportQuotaFile = exportFileFactory(exportQuota)
export const exportQuotaReportFile = exportFileFactory(exportQuotaReport)
export const exportCheckingPricesFile = exportFileFactory(exportCheckingPrices)
export const exportTechCheckingFile = exportFileFactory(exportTechChecking)
export const exportCompareArchivesFile = exportFileFactory(exportCompareArchives)
export const exportArchivesItemFile = exportFileFactory(exportArchivesItem)
export const exportMonitoringValidationStoresLinksFile = exportFileFactory(exportMonitoringValidationStoresLinks)
export const exportAllMonitoringsRegistryFile = exportFileFactory(exportAllMonitoringsRegistry)
export const exportTemplateMonitoringListsFile = exportFileFactory(exportTemplateMonitoringLists)
export const exportAllMonitoringTasksFile = exportFileFactory(exportAllMonitoringTasks)
export const exportAllMonitoringResultsFile = exportFileFactory(
  createAwaitReportCompletionFn(exportAllMonitoringResults)
)
export const exportTemplateQuotaReportFile = exportFileFactory(exportTemplateQuotaReport)
export const exportMonitoringCategoriesKindsListFile = exportFileFactory(exportMonitoringCategoriesKinds)
